<script setup lang="ts">
import { useExclusiveManagerQuery } from '~/api/queries/useExclusiveManagerQuery';

// Exclusive Manager contact (ID-176): sidebar card on exclusive projects, strip above the 100% Commission list.
const { variant = 'card' } = defineProps<{
  variant?: 'card' | 'strip';
}>();

const { t } = useI18n();
const { data: manager } = useExclusiveManagerQuery(true);

// A broken photo link falls back to the initials.
const photoFailed = ref(false);
watch(
  () => manager.value?.photoUrl,
  () => (photoFailed.value = false),
);
const photo = computed(() => (!photoFailed.value && manager.value?.photoUrl) || null);

const initials = computed(() => {
  const parts = (manager.value?.name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return `${parts[0][0] ?? ''}${parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''}`.toUpperCase();
});
</script>

<template>
  <section
    v-if="manager"
    :class="[
      'rounded-[20px]',
      variant === 'strip'
        ? 'flex flex-col gap-3 border border-black/[0.06] bg-white p-4 tablet:flex-row tablet:items-center tablet:gap-4'
        : 'bg-bg p-5',
    ]"
    data-testid="exclusive-manager"
  >
    <div class="flex min-w-0 flex-1 items-center gap-3">
      <img
        v-if="photo"
        :src="photo"
        alt=""
        class="size-12 shrink-0 rounded-full object-cover"
        data-testid="exclusive-manager-photo"
        @error="photoFailed = true"
      />
      <div
        v-else
        class="inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gold-primary-100 to-gold-primary-120 font-display text-[16px] font-extrabold text-black-100"
        aria-hidden="true"
      >
        {{ initials }}
      </div>
      <div class="min-w-0">
        <p class="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-black-60">
          {{ t('exclusives.manager.label') }}
        </p>
        <p class="font-serif text-[17px] font-semibold tracking-[-0.01em] text-black-100">
          {{ manager.name }}
        </p>
        <p
          class="text-[13px] font-semibold text-black-80"
          dir="ltr"
        >
          {{ manager.phone }}
        </p>
        <p class="mt-0.5 text-[12px] text-black-60">
          {{ t('exclusives.manager.hint') }}
        </p>
      </div>
    </div>

    <div :class="['flex gap-2', variant === 'strip' ? 'tablet:shrink-0' : 'mt-4']">
      <a
        :href="manager.whatsappUrl"
        target="_blank"
        rel="noopener"
        class="inline-flex h-[42px] flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#25D366] px-4 text-[13px] font-bold text-white transition-colors hover:bg-[#1EB454]"
      >
        <UIIcon
          name="whatsapp"
          size="sm"
        />
        {{ t('exclusives.manager.whatsapp') }}
      </a>
      <a
        :href="manager.callUrl"
        class="inline-flex h-[42px] flex-1 items-center justify-center gap-2 rounded-[10px] border border-black/[0.08] bg-white px-4 text-[13px] font-semibold text-black-100 transition-colors hover:bg-black-05"
        :aria-label="t('exclusives.manager.callAria', { name: manager.name, phone: manager.phone })"
      >
        <UIIcon
          name="phone"
          size="sm"
        />
        {{ t('exclusives.manager.call') }}
      </a>
    </div>
  </section>
</template>
